/** Angular imports */
import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, Signal, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

/** Local imports */
import { PlaylistReports, SingleMediaLibraryTabs } from '@features/media-library/interfaces';
import { FileSizePipe } from '@shared/pipes';

/** Component imports */
import { Button } from '@ntv360/component-pantry';
import { TableColumn, TableComponent } from '@shared';

/** Constants imports */
import { MainLayoutStore } from '@layouts/main-layout';
import { CONTENT_INFO_BATCH_ONE, CONTENT_INFO_BATCH_TWO, REPORTS_CARD_TABS } from '../../constants';

/** Sample ONLY while no backend yet */
import { MediaLibraryService } from '@core';
import { ApiContent } from '@core/services/media-library/media-library.interface';
import {
    ITEMS,
    PLAYLIST_REPORTS_HISTORY_DATA,
    PLAYLIST_REPORTS_PLAYING_WHERE_DATA,
    PLAYLIST_REPORTS_PLAYLIST_DATA,
    PLAYLIST_REPORTS_REPORTS_DATA,
} from '@features/media-library/data';
import { Logger, withLoading } from '@shared/utils';
import { catchError, EMPTY } from 'rxjs';

@Component({
    selector: 'app-single-media-library',
    imports: [Button, TableComponent],
    providers: [DatePipe],
    templateUrl: './single-media-library.component.html',
    styleUrl: './single-media-library.component.scss',
})
export class SingleMediaLibraryComponent implements OnInit, OnDestroy {
    /**
     * Inject router service for programmatic navigation
     */
    private route = inject(ActivatedRoute);

    /**
     * Inject media library service for api calls
     */
    private mediaLibraryService = inject(MediaLibraryService);

    /**
     * Inject date pipe for date formatting
     */
    private datePipe = inject(DatePipe);

    /**
     * Content Info structure
     */
    protected contentInfo = CONTENT_INFO_BATCH_ONE;
    protected contentInfo2 = CONTENT_INFO_BATCH_TWO;

    /**
     * Report tabs constants
     */
    protected readonly REPORTS_CARD_TABS = REPORTS_CARD_TABS;

    /**
     * Content item ID, extracted from route param
     */
    public itemId = signal<string | null>(null);

    /**
     * Holds the current content item data
     */
    public item = signal<(ApiContent & { classification: string }) | null>(null);

    /**
     * Current viewing playlist report tab e.g. "reports", "in-playlist", "playing-where", "history". Defaults to `reports`
     */
    public currentReportTab = signal<SingleMediaLibraryTabs>(SingleMediaLibraryTabs.REPORTS);

    /**
     * Current viewing playlist report tab data
     */
    protected currentReportTabData = signal<PlaylistReports[]>([]);

    /**
     * Flag whether item data fetching is underway
     */
    protected isLoadingItemData = signal<boolean>(false);

    /**
     * Get table columns for the selected or current viewing tab
     */
    protected getTableColumns: Signal<TableColumn[]> = computed(() => {
        return (
            this.REPORTS_CARD_TABS.find((tab) => tab.id === this.currentReportTab())?.columns || []
        );
    });

    /**
     * Computed playlist report tab values
     */
    protected playlistReportTabData: Signal<PlaylistReports[]> = computed(() => {
        switch (this.currentReportTab()) {
            case SingleMediaLibraryTabs.REPORTS:
                return PLAYLIST_REPORTS_REPORTS_DATA;
            case SingleMediaLibraryTabs.IN_PLAYLIST:
                return PLAYLIST_REPORTS_PLAYLIST_DATA;
            case SingleMediaLibraryTabs.PLAYING_WHERE:
                return PLAYLIST_REPORTS_PLAYING_WHERE_DATA;
            case SingleMediaLibraryTabs.HISTORY:
                return PLAYLIST_REPORTS_HISTORY_DATA;
            default:
                return [];
        }
    });

    /**
     * Extracts content item ID from route param
     */
    constructor() {
        this.itemId.set(this.route.snapshot.params['id']);
    }

    /**
     * Sets current viewing report tab
     */
    protected setCurrentReportTab(tabId: string) {
        this.currentReportTab.set(tabId as SingleMediaLibraryTabs);
    }

    /**
     * Fetches item data and sets breadcrumb title
     */
    public ngOnInit(): void {
        this.loadItemData();

        // Update breadcrumb
        MainLayoutStore.setBreadcrumb(this.item()?.fileName || 'Fetching...');
    }

    /**
     * Clear breadcrumb on destory
     */
    ngOnDestroy(): void {
        MainLayoutStore.clearBreadcrumb();
    }

    /**
     * Maps API response to UI requirements
     */
    private mapDataToUi(item: ApiContent): void {
        this.item.set({
            ...item,
            classification: this.getContentType(item.fileType),
        });
    }

    /** ======================= Utilities ========================== */

    /**
     * Helper to resolve object values from keys
     */
    protected resolveValue(key: string | undefined, type?: string): string {
        if (!key) return '--';
        const item = this.item();
        if (!item) return '--';

        // Extract value using type-safe indexed access
        const value = (item as unknown as Record<string, unknown>)[key];

        // Specific handling for some keys if needed
        if (key === 'isActive') return value ? 'Active' : 'Inactive';
        if (key === 'createdBy') return (value as string) || 'System';

        if (type === 'date' && value) {
            return this.datePipe.transform(value as string, 'MMMM dd, yyyy') || '--';
        }

        return (value as string | number)?.toString() || '--';
    }

    /** Get content type based on file type
     * @param content The content item
     * @returns {string} The content type
     */
    protected getContentType(fileType: string): string {
        if (fileType.includes('video') || fileType.includes('webm')) {
            return 'video';
        } else if (fileType.includes('image')) {
            return 'image';
        } else {
            return 'image';
        }
    }

    /** ============================= API Calls ============================= */

    /**
     * Load item data
     */
    private loadItemData() {
        const itemId = this.itemId();
        if (!itemId) return;
        this.mediaLibraryService
            .getContentById(itemId)
            .pipe(
                withLoading(this.isLoadingItemData),
                catchError((error: unknown) => {
                    Logger.error('SingleMediaLibrary', 'error fetching item details', error);
                    return EMPTY;
                }),
            )
            .subscribe((response) => {
                this.mapDataToUi(response.data);
            });
    }
}
